/**
 * Top-level route tree.
 *
 * Layout:
 *   - ``/auth/google/finish`` + ``/auth/reset`` are sibling routes at
 *     the top of the tree because they must render regardless of
 *     auth state (Google completes the sign-in mid-render; reset
 *     confirm runs from an email link without a session).
 *   - Everything else flows through ``AuthGate``: when not
 *     authenticated, ``AuthRoutes`` handles ``/login`` / ``/signup`` /
 *     ``/reset-request`` and a redirect-to-login catchall. When
 *     authenticated, ``AppShell`` renders and the child routes
 *     dispatch to per-screen components inside its ``<Outlet />``.
 */

import { Navigate, Outlet, createBrowserRouter } from "react-router-dom";

import { AuthRoutes } from "../auth/AuthRoutes";
import { GoogleFinishPage } from "../auth/GoogleFinishPage";
import { PasswordResetConfirmScreen } from "../auth/PasswordResetConfirmScreen";
import { useAuth } from "../auth/AuthProvider";
import { AppShell } from "./AppShell";
import { RequireAdmin } from "./RequireAdmin";
import { RequireAuth } from "./RequireAuth";

/** Renders the auth routes when logged out; otherwise lets the
 *  authed branch render. Used as the "everything else" element. */
function AuthGate(): JSX.Element {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Outlet /> : <AuthRoutes />;
}

export const router = createBrowserRouter([
  { path: "/auth/google/finish", element: <GoogleFinishPage /> },
  { path: "/auth/reset", element: <PasswordResetConfirmScreen /> },
  {
    path: "*",
    element: <AuthGate />,
    children: [
      {
        element: <RequireAuth />,
        children: [
          {
            element: <AppShell />,
            children: [
              { index: true, element: <Navigate to="/today" replace /> },
          {
            path: "today",
            lazy: async () => ({
              Component: (await import("../today/TodayScreen")).TodayScreen,
            }),
          },
          {
            path: "notes",
            lazy: async () => ({
              Component: (await import("../notes/NotesScreen")).NotesScreen,
            }),
            children: [{ path: ":noteId" }],
          },
          {
            path: "tasks",
            lazy: async () => ({
              Component: (await import("../tasks/TasksScreen")).TasksScreen,
            }),
            children: [
              {
                path: ":listId",
                children: [{ path: ":taskId" }],
              },
            ],
          },
          {
            path: "calendar",
            lazy: async () => ({
              Component: (await import("../calendar/CalendarScreen")).CalendarScreen,
            }),
            children: [
              {
                path: ":date",
                children: [{ path: ":eventId" }],
              },
            ],
          },
          {
            path: "habits",
            lazy: async () => ({
              Component: (await import("../habits/HabitsScreen")).HabitsScreen,
            }),
            children: [{ path: ":habitId" }],
          },
          {
            path: "shopping",
            lazy: async () => ({
              Component: (await import("../shopping/ShoppingScreen")).ShoppingScreen,
            }),
            children: [
              {
                path: ":listId",
                children: [{ path: ":itemId" }],
              },
            ],
          },
          {
            path: "recipes",
            lazy: async () => ({
              Component: (await import("../recipes/RecipesScreen")).RecipesScreen,
            }),
            children: [
              { path: "new" },
              { path: ":recipeId" },
            ],
          },
          {
            path: "settings",
            lazy: async () => ({
              Component: (await import("../settings/SettingsScreen")).SettingsScreen,
            }),
          },
          {
            path: "people",
            lazy: async () => ({
              Component: (await import("../people/PeopleScreen")).PeopleScreen,
            }),
          },
          {
            path: "people/add",
            lazy: async () => ({
              Component: (await import("../people/AddFriendScreen")).AddFriendScreen,
            }),
          },
          {
            path: "people/requests",
            lazy: async () => ({
              Component: (await import("../people/RequestsScreen")).RequestsScreen,
            }),
          },
          {
            path: "people/:userId",
            lazy: async () => ({
              Component: (await import("../people/PersonDetail")).PersonDetail,
            }),
          },
          {
            path: "admin",
            element: <RequireAdmin />,
            children: [
              { index: true, element: <Navigate to="/admin/users" replace /> },
              {
                path: "users",
                lazy: async () => ({
                  Component: (await import("../admin/UsersScreen")).UsersScreen,
                }),
              },
              {
                path: "users/:userId",
                lazy: async () => ({
                  Component: (await import("../admin/UserDetailScreen"))
                    .UserDetailScreen,
                }),
              },
              {
                path: "audit-log",
                lazy: async () => ({
                  Component: (await import("../admin/AuditLogScreen"))
                    .AuditLogScreen,
                }),
              },
            ],
          },
              { path: "*", element: <Navigate to="/today" replace /> },
            ],
          },
        ],
      },
    ],
  },
]);
